import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BOARD_DISPLAY_NAME,
  IS_MIX_CATALOG,
  MIX_BOARD_NAMES,
  MIX_REGISTERED_SLUGS,
} from "@/config/browserBoard";
import {
  getArchivePageNumbers,
  getArchivePageRedirect,
  normalizeArchivePage,
  normalizeArchiveTotalPages,
} from "@/features/archive/archivePagination";
import { ArchiveSearchBar } from "@/features/archive/components/ArchiveSearchBar";
import { ArchiveStorageGauge } from "@/features/archive/components/ArchiveStorageGauge";
import { DesktopArchiveGrid } from "@/features/archive/components/desktop/DesktopArchiveGrid";
import { DesktopArchiveToolbar } from "@/features/archive/components/desktop/DesktopArchiveToolbar";
import { ArchiveGrid } from "@/features/archive/components/lists/ArchiveGrid";
import {
  useArchiveStorage,
  useArchiveStorages,
} from "@/features/archive/hooks/useArchiveStorage";
import { useArchiveThreads } from "@/features/archive/hooks/useArchiveThreads";
import {
  getCatalogPageLimit,
  useCatalogStore,
} from "@/features/catalog/stores/catalogStore";
import {
  DesktopHistorySidebar,
  type DesktopHistorySidebarHandle,
} from "@/features/history/components/DesktopHistorySidebar";
import {
  normalizeSearchScope,
  type SearchScope,
  serializeSearchScope,
} from "@/features/search/searchScope";
import { DesktopSettingsModal } from "@/features/settings/components/desktop/DesktopSettingsModal";
import {
  resolveEnabledMixSlugs,
  useMixStore,
} from "@/features/settings/stores/mixStore";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import { useManualRefresh } from "@/shared/hooks/useManualRefresh";
import { useIsDesktop } from "@/shared/hooks/useMediaQuery";
import { hasLinkModifierKey } from "@/shared/lib/keyboardAccessibility";
import { LoadingScreen, RefreshStatus } from "@/shared/ui/feedback";
import { QueryStateBoundary } from "@/shared/ui/feedback/QueryStateBoundary";
import { Button } from "@/shared/ui/form";

interface ArchiveQueryStateProps {
  children: React.ReactNode;
  error: unknown;
  hasData: boolean;
  isLoading: boolean;
  isRetrying: boolean;
  onRetry: () => void;
}

interface ArchivePageLayoutProps {
  children: React.ReactNode;
  error: unknown;
  hasData: boolean;
  isDesktop: boolean;
  isFetching: boolean;
  isLoading: boolean;
  isManualRefreshing: boolean;
  isRetrying: boolean;
  isSettingsOpen: boolean;
  searchQuery: string;
  searchScope: SearchScope;
  onClearSearch: () => void;
  onCloseSettings: () => void;
  onRefresh: () => void;
  onRetry: () => void;
  onSearch: (query: string) => void;
  onSearchScopeChange: (scope: SearchScope) => void;
  onScrollToBottom: () => void;
  onScrollToTop: () => void;
  onSettingsClick: React.MouseEventHandler<HTMLAnchorElement>;
}

export const ArchiveQueryState: React.FunctionComponent<
  ArchiveQueryStateProps
> = ({
  children,
  error,
  hasData,
  isLoading,
  isRetrying,
  onRetry,
}: ArchiveQueryStateProps) => (
  <QueryStateBoundary
    error={error}
    hasData={hasData}
    isLoading={isLoading}
    isRetrying={isRetrying}
    message="過去ログの読み込みに失敗しました"
    onRetry={onRetry}
  >
    {children}
  </QueryStateBoundary>
);

export const ArchivePageLayout: React.FunctionComponent<
  ArchivePageLayoutProps
> = ({
  children,
  error,
  hasData,
  isDesktop,
  isFetching,
  isLoading,
  isManualRefreshing,
  isRetrying,
  isSettingsOpen,
  searchQuery,
  searchScope,
  onClearSearch,
  onCloseSettings,
  onRefresh,
  onRetry,
  onSearch,
  onSearchScopeChange,
  onScrollToBottom,
  onScrollToTop,
  onSettingsClick,
}: ArchivePageLayoutProps) => {
  const desktopHistorySidebarRef = useRef<DesktopHistorySidebarHandle>(null);
  useDocumentTitle(`過去ログ - ${BOARD_DISPLAY_NAME}`);

  if (isDesktop) {
    return (
      <section
        aria-labelledby="archive-page-title"
        className="desktop-catalog-page"
      >
        <DesktopArchiveToolbar
          isFetching={isFetching}
          searchQuery={searchQuery}
          searchScope={searchScope}
          onClearSearch={onClearSearch}
          onRefresh={onRefresh}
          onSearch={onSearch}
          onSearchScopeChange={onSearchScopeChange}
          onToggleSidebar={(): void =>
            desktopHistorySidebarRef.current?.toggle()
          }
          onScrollToTop={onScrollToTop}
          onScrollToBottom={onScrollToBottom}
          onSettingsClick={onSettingsClick}
        />
        <RefreshStatus isRefreshing={isManualRefreshing} />
        <h1 id="archive-page-title" className="desktop-mode-title">
          過去ログ
        </h1>
        <ArchiveQueryState
          error={error}
          hasData={hasData}
          isLoading={isLoading}
          isRetrying={isRetrying}
          onRetry={onRetry}
        >
          {children}
        </ArchiveQueryState>
        <DesktopHistorySidebar
          ref={desktopHistorySidebarRef}
          showClosedHandle={false}
        />
        <DesktopSettingsModal
          isOpen={isSettingsOpen}
          onClose={onCloseSettings}
        />
      </section>
    );
  }

  return (
    <section aria-labelledby="archive-page-title">
      <header className="px-4 pt-4">
        <h1
          id="archive-page-title"
          className="text-xl font-bold text-foreground"
        >
          過去ログ
        </h1>
        <ArchiveSearchBar
          className="mt-3"
          isFetching={isFetching}
          query={searchQuery}
          scope={searchScope}
          onClear={onClearSearch}
          onScopeChange={onSearchScopeChange}
          onSubmit={onSearch}
        />
      </header>
      <ArchiveQueryState
        error={error}
        hasData={hasData}
        isLoading={isLoading}
        isRetrying={isRetrying}
        onRetry={onRetry}
      >
        {children}
      </ArchiveQueryState>
    </section>
  );
};

export const ArchivePage: React.FunctionComponent = () => {
  const {
    page = 1,
    q = "",
    scope: serializedScope,
  } = useSearch({
    from: "/archive",
  });
  const navigate = useNavigate({ from: "/archive" });
  const isDesktop = useIsDesktop();
  const columns = useCatalogStore((state) => state.columns);
  const rows = useCatalogStore((state) => state.rows);
  const imageSize = useCatalogStore((state) => state.imageSize);
  const archivePageLimit = getCatalogPageLimit(columns, rows);
  const archiveSearchQuery = q;
  const archiveSearchScope = normalizeSearchScope(serializedScope);
  const archiveSearchScopeParam = serializeSearchScope(archiveSearchScope);
  const archiveQuery = useArchiveThreads(
    page,
    archivePageLimit,
    archiveSearchQuery,
    archiveSearchScope,
  );
  const mixBoardSettings = useMixStore((state) => state.boardSettings);
  const selectedMixSlugs = useMemo(
    () =>
      IS_MIX_CATALOG
        ? resolveEnabledMixSlugs(MIX_REGISTERED_SLUGS, mixBoardSettings)
        : [],
    [mixBoardSettings],
  );
  const storageQuery = useArchiveStorage(!IS_MIX_CATALOG);
  const mixStorageQueries = useArchiveStorages(
    selectedMixSlugs,
    IS_MIX_CATALOG,
  );
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [prevIsDesktop, setPrevIsDesktop] = useState(isDesktop);
  if (isDesktop !== prevIsDesktop) {
    setPrevIsDesktop(isDesktop);
    if (!isDesktop) setIsSettingsOpen(false);
  }
  const refreshArchive = useCallback(async (): Promise<void> => {
    if (IS_MIX_CATALOG) {
      await Promise.all([
        archiveQuery.refetch(),
        ...mixStorageQueries.map((query) => query.refetch()),
      ]);
      return;
    }
    await Promise.all([archiveQuery.refetch(), storageQuery.refetch()]);
  }, [archiveQuery.refetch, mixStorageQueries, storageQuery.refetch]);
  const isFetching =
    archiveQuery.isFetching ||
    (IS_MIX_CATALOG
      ? mixStorageQueries.some((query) => query.isFetching)
      : storageQuery.isFetching);
  const { isManualRefreshing, refreshManually } = useManualRefresh(
    refreshArchive,
    isFetching,
  );
  const totalPages = normalizeArchiveTotalPages(
    archiveQuery.data?.pagination.totalPages ?? 1,
  );
  const currentPage = normalizeArchivePage(page, totalPages);
  const redirectPage = archiveQuery.data
    ? getArchivePageRedirect(page, totalPages)
    : null;

  useEffect(() => {
    if (redirectPage === null) return;
    void navigate({
      search: {
        page: redirectPage,
        q: archiveSearchQuery || undefined,
        scope: archiveSearchScopeParam,
      },
      replace: true,
    });
  }, [archiveSearchQuery, archiveSearchScopeParam, navigate, redirectPage]);

  const changePage = (nextPage: number): void => {
    void navigate({
      search: {
        page: nextPage,
        q: archiveSearchQuery || undefined,
        scope: archiveSearchScopeParam,
      },
    });
  };
  const searchArchive = (query: string): void => {
    void navigate({
      search: { page: 1, q: query, scope: archiveSearchScopeParam },
    });
  };
  const clearArchiveSearch = (): void => {
    void navigate({
      search: { page: 1, q: undefined, scope: archiveSearchScopeParam },
    });
  };
  const changeArchiveSearchScope = (scope: SearchScope): void => {
    void navigate({
      search: {
        page: 1,
        q: archiveSearchQuery || undefined,
        scope: serializeSearchScope(scope),
      },
    });
  };
  const pagination = totalPages > 1 && (
    <nav
      aria-label="過去ログのページ移動"
      className="flex flex-wrap items-center justify-center gap-2 px-4 py-3"
    >
      <Button
        variant="default"
        disabled={currentPage <= 1}
        onClick={(): void => changePage(1)}
      >
        先頭
      </Button>
      <Button
        variant="default"
        disabled={currentPage <= 1}
        onClick={(): void => changePage(currentPage - 1)}
      >
        前へ
      </Button>
      {getArchivePageNumbers(currentPage, totalPages).map((number) => (
        <Button
          key={number}
          variant={number === currentPage ? "primary" : "default"}
          onClick={(): void => changePage(number)}
          aria-current={number === currentPage ? "page" : undefined}
        >
          {number}
        </Button>
      ))}
      <Button
        variant="default"
        disabled={currentPage >= totalPages}
        onClick={(): void => changePage(currentPage + 1)}
      >
        次へ
      </Button>
      <Button
        variant="default"
        disabled={currentPage >= totalPages}
        onClick={(): void => changePage(totalPages)}
      >
        末尾
      </Button>
    </nav>
  );

  const data = archiveQuery.data;
  const archiveContent = data ? (
    redirectPage !== null ? (
      <LoadingScreen />
    ) : (
      <>
        {IS_MIX_CATALOG
          ? mixStorageQueries.map((query, index) => {
              const slug = selectedMixSlugs[index];
              return slug ? (
                <ArchiveStorageGauge
                  key={slug}
                  boardName={MIX_BOARD_NAMES[slug] ?? slug}
                  hasError={query.isError}
                  storage={query.data}
                />
              ) : null;
            })
          : /* 専用エンドポイントが失敗しても一覧レスポンス埋め込みの容量情報で継続する */
            (storageQuery.data ?? data.storage) && (
              <ArchiveStorageGauge
                storage={storageQuery.data ?? data.storage}
              />
            )}
        {archiveSearchQuery && (
          <p
            className="px-4 py-2 text-sm text-muted-foreground"
            aria-live="polite"
          >
            「{archiveSearchQuery}」の
            {archiveSearchScope === "op" ? "スレ文" : "全文"}
            の検索結果: {data.pagination.total}件
          </p>
        )}
        {pagination}
        {isDesktop ? (
          <DesktopArchiveGrid
            threads={data.threads}
            columns={columns}
            imageSize={imageSize}
            emptyMessage={
              archiveSearchQuery ? "一致する過去ログはありません" : undefined
            }
          />
        ) : (
          <ArchiveGrid
            threads={data.threads}
            columns={columns}
            emptyMessage={
              archiveSearchQuery ? "一致する過去ログはありません" : undefined
            }
          />
        )}
        {pagination}
      </>
    )
  ) : null;

  return (
    <ArchivePageLayout
      error={archiveQuery.error}
      hasData={data !== undefined}
      isDesktop={isDesktop}
      isFetching={isFetching}
      isLoading={archiveQuery.isLoading}
      isManualRefreshing={isManualRefreshing}
      isRetrying={isFetching}
      isSettingsOpen={isSettingsOpen}
      searchQuery={archiveSearchQuery}
      searchScope={archiveSearchScope}
      onCloseSettings={(): void => setIsSettingsOpen(false)}
      onClearSearch={clearArchiveSearch}
      onRefresh={(): void => void refreshManually()}
      onRetry={(): void => void refreshArchive()}
      onSearch={searchArchive}
      onSearchScopeChange={changeArchiveSearchScope}
      onScrollToBottom={(): void =>
        window.scrollTo({ top: document.documentElement.scrollHeight })
      }
      onScrollToTop={(): void => window.scrollTo({ top: 0 })}
      onSettingsClick={(event: React.MouseEvent<HTMLAnchorElement>): void => {
        if (hasLinkModifierKey(event)) return;
        event.preventDefault();
        setIsSettingsOpen(true);
      }}
    >
      {archiveContent}
    </ArchivePageLayout>
  );
};
